/* Admin · Shared History tab — who has been sent which mandate and
   whether they have completed it. Resend / Revoke per row. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Ban, Loader2, ExternalLink, Hash } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = ["sent", "opened", "completed", "verified", "rejected", "revoked"];
const STYLE = {
  sent:      { bg: "#3A4048", fg: "#E4E6E8", label: "Sent" },
  opened:    { bg: "#2A3A4E", fg: "#9FD0F5", label: "Opened" },
  completed: { bg: "#3B3418", fg: "#E8CB6A", label: "Completed · Filed" },
  verified:  { bg: "#1F3B2E", fg: "#7BD8AA", label: "Verified" },
  rejected:  { bg: "#40232A", fg: "#F2A6B1", label: "Rejected" },
  revoked:   { bg: "#2A2A2A", fg: "#9A9A9A", label: "Revoked" },
};

export function BureaucraticSharedHistory({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(true);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/bureaucratic/shares`, {
        params: { status: status || undefined }, withCredentials: true,
      });
      setItems(data.items || []); setCounts(data.counts || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Unable to load shared history"); }
    finally { setBusy(false); }
  }, [status]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (id, verb) => {
    if (verb === "revoke" && !window.confirm("Revoke this mandate link? The customer will no longer be able to open it.")) return;
    setActing(`${id}:${verb}`);
    try {
      await axios.post(`${API}/admin/bureaucratic/shares/${id}/${verb}`, {}, { withCredentials: true });
      toast.success(verb === "resend" ? "Mandate email re-sent" : "Mandate link revoked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || `${verb} failed`); }
    finally { setActing(null); }
  };

  return (
    <div data-testid="shared-history">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <FilterChip active={status === ""} onClick={() => setStatus("")} label="All" n={Object.values(counts).reduce((a, b) => a + b, 0)} testid="all" />
        {STAGES.map((s) => (
          <FilterChip key={s} active={status === s} onClick={() => setStatus(s)} label={STYLE[s].label} n={counts[s] || 0} testid={s} />
        ))}
      </div>

      {busy ? (
        <div className="text-center py-20 text-[#6B6F76] font-mono text-sm">Loading shared history…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-[#6B6F76] font-mono text-sm border border-dashed" style={{ borderColor: "#C4C8CC" }}>
          No mandates shared yet. Use SHARE TO CUSTOMER on any template card.
        </div>
      ) : (
        <div className="border bg-white/60 overflow-x-auto" style={{ borderColor: "#C4C8CC" }}>
          <table className="w-full text-left">
            <thead>
              <tr className="border-b" style={{ borderColor: "#C4C8CC", background: "#E4E6E8" }}>
                {["VNC-SRN", "Instrument", "Recipient", "Vinci Code", "Sent", "Progress", "Status", ""].map((h) => (
                  <th key={h} className="px-3 py-2.5 font-mono text-[9px] uppercase tracking-[0.24em] text-[#4A5058] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: "#E4E6E8" }}>
              {items.map((s) => <ShareRow key={s.id} s={s} acting={acting} onAct={act} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, n, testid }) {
  return (
    <button onClick={onClick} data-testid={`history-filter-${testid}`}
            className={`font-mono text-[9px] uppercase tracking-[0.24em] px-3 py-1.5 rounded-sm border transition-colors ${
              active ? "bg-[#1F2226] text-[#E4E6E8] border-[#1F2226]" : "border-[#7A8590]/50 text-[#4A5058] hover:bg-[#E4E6E8]"}`}>
      {label} <span className={active ? "text-[#D8B23E]" : "text-[#8A9098]"}>{n}</span>
    </button>
  );
}

function ShareRow({ s, acting, onAct }) {
  const st = STYLE[s.status] || { bg: "#3A4048", fg: "#E4E6E8", label: s.status };
  const canResend = ["sent", "opened"].includes(s.status);
  const canRevoke = ["sent", "opened"].includes(s.status);
  const stageIdx = { sent: 0, opened: 1, completed: 2, verified: 3, rejected: 3 }[s.status] ?? -1;
  return (
    <tr data-testid={`history-row-${s.id}`} className="hover:bg-white/80 transition-colors">
      <td className="px-3 py-2.5 whitespace-nowrap">
        <span className="inline-flex items-center gap-1 font-mono text-[11px] text-[#8A6D1F] font-bold tracking-[0.08em]" data-testid={`history-srn-${s.id}`}>
          <Hash size={10} /> {s.srn}
        </span>
      </td>
      <td className="px-3 py-2.5 min-w-[200px]">
        <div className="font-serif text-[13px] text-[#161A1E] leading-snug">{s.title}</div>
        <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#8A9098]">{s.generic ? "Standard mandate" : "Bespoke form"}</div>
      </td>
      <td className="px-3 py-2.5">
        <div className="text-[12px] font-mono text-[#161A1E]">{s.recipient_email}</div>
        <div className="text-[10px] text-[#6B6F76]">{s.recipient_name}</div>
      </td>
      <td className="px-3 py-2.5 font-mono text-[11px] text-[#2C3138] whitespace-nowrap">{s.vinci_code || "—"}</td>
      <td className="px-3 py-2.5 font-mono text-[10px] text-[#6B6F76] whitespace-nowrap">
        {(s.sent_at || "").slice(0, 10)}{s.resend_count ? ` · ×${s.resend_count + 1}` : ""}
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-1" title="Sent → Opened → Completed → Adjudicated">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="h-1.5 w-6 rounded-full"
                  style={{ background: i <= stageIdx ? (s.status === "rejected" && i === 3 ? "#B23A48" : "#1F2226") : "#D6D8DB" }} />
          ))}
        </div>
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-1 rounded-sm" style={{ background: st.bg, color: st.fg }}
              data-testid={`history-status-${s.id}`}>{st.label}</span>
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <div className="flex items-center justify-end gap-2">
          {s.submission_id && (
            <a href={`${API}/bureaucratic/corrections/${s.submission_id}/snapshot`} target="_blank" rel="noreferrer"
               data-testid={`history-snapshot-${s.id}`}
               className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#2C3138] hover:text-black">
              <ExternalLink size={10} /> Snapshot
            </a>
          )}
          {canResend && (
            <button onClick={() => onAct(s.id, "resend")} disabled={!!acting}
                    data-testid={`history-resend-${s.id}`}
                    className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#2C3138] hover:text-black disabled:opacity-40">
              {acting === `${s.id}:resend` ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} Resend
            </button>
          )}
          {canRevoke && (
            <button onClick={() => onAct(s.id, "revoke")} disabled={!!acting}
                    data-testid={`history-revoke-${s.id}`}
                    className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#B23A48] hover:text-[#7f2431] disabled:opacity-40">
              {acting === `${s.id}:revoke` ? <Loader2 size={10} className="animate-spin" /> : <Ban size={10} />} Revoke
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
